import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import { isIOSDevice, getSpeakerLang } from "@/utils/device-detection";
import { useI18n } from "@/lib/i18n";
import { vocabularyLevelChecker } from "@/lib/vocabularyLevel";
import { getAllPossibleLemmas } from "@/lib/vocabularyLevel/word-lemmatizer";
import { doSpeak, defaultTTSProvider, langCode2TTSLang } from "@/components/tts";
import type { LangCode } from "@/components/tts/types";

interface VocabCardProps {
  word: string;
  pronunciation?: string;
  translation?: string;
  lemma?: string | null;
  onCorrect?: () => void;
  onAutoComplete?: () => void;
  autoFocus?: boolean;
  isInitiallyCompleted?: boolean;
  feedbackVolume?: number;
  languageSetting?: string;
}

export const VocabCard = React.forwardRef<HTMLDivElement, VocabCardProps>(({
  word,
  translation,
  lemma,
  onCorrect,
  onAutoComplete,
  autoFocus,
  isInitiallyCompleted = false,
  feedbackVolume = 1,
  languageSetting = 'english'
}, ref) => {
  const { t } = useI18n();
  const [userInputs, setUserInputs] = useState<string[]>(
    isInitiallyCompleted ? word.split('') : Array(word.length).fill('')
  );
  const [focusIndex, setFocusIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(isInitiallyCompleted);
  const [errorIndex, setErrorIndex] = useState<number | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [fullWordInput, setFullWordInput] = useState(isInitiallyCompleted ? word : '');

  const containerRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const beepAudioRef = useRef<HTMLAudioElement | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const singleInputRef = useRef<HTMLInputElement>(null);
  const typingSoundPoolRef = useRef<HTMLAudioElement[]>([]);
  const beepSoundPoolRef = useRef<HTMLAudioElement[]>([]);
  const typingSoundIndexRef = useRef(0);
  const beepSoundIndexRef = useRef(0);
  const TYPING_POOL_INITIAL_SIZE = 4;
  const BEEP_POOL_INITIAL_SIZE = 3;
  const TYPING_POOL_MAX_SIZE = 6;
  const BEEP_POOL_MAX_SIZE = 4;
  const audioContextRef = useRef<AudioContext | null>(null);
  const typingBufferRef = useRef<AudioBuffer | null>(null);
  const beepBufferRef = useRef<AudioBuffer | null>(null);
  const speakAbortRef = useRef<AbortController | null>(null);
  const clampedFeedbackVolume = React.useMemo(() => {
    if (typeof feedbackVolume !== 'number' || Number.isNaN(feedbackVolume)) {
      return 1;
    }
    return Math.min(Math.max(feedbackVolume, 0), 1);
  }, [feedbackVolume]);
  const resolveLemmaSync = useCallback((): { value: string | null; source: 'dictionary' | 'heuristic' | null } => {
    try {
      if (lemma && lemma.toLowerCase() !== word.toLowerCase()) {
        return { value: lemma, source: 'dictionary' };
      }
    } catch (error) {
      console.warn(`Failed to use provided lemma for "${word}":`, error);
    }

    try {
      const candidate = vocabularyLevelChecker?.getLemma?.(word);
      if (candidate && candidate.toLowerCase() !== word.toLowerCase()) {
        return { value: candidate, source: 'dictionary' };
      }
    } catch (error) {
      console.warn(`Failed to resolve lemma via dictionary for "${word}":`, error);
    }

    try {
      const candidates = getAllPossibleLemmas(word.toLowerCase()) || [];
      const heuristic = candidates.find(candidate => candidate && candidate !== word.toLowerCase());
      if (heuristic) {
        return { value: heuristic, source: 'heuristic' };
      }
    } catch (error) {
      console.warn(`Failed to resolve lemma heuristically for "${word}":`, error);
    }

    return { value: null, source: null };
  }, [lemma, word]);

  const [normalizedLemma, setNormalizedLemma] = useState<{ value: string | null; source: 'dictionary' | 'heuristic' | null }>(() => resolveLemmaSync());

  useEffect(() => {
    let cancelled = false;
    const syncLemmaInfo = resolveLemmaSync();
    setNormalizedLemma(syncLemmaInfo);

    const needsDictionaryRefresh = syncLemmaInfo.source !== 'dictionary';
    if (needsDictionaryRefresh && vocabularyLevelChecker?.hasTranslationAsync) {
      (async () => {
        try {
          await vocabularyLevelChecker.hasTranslationAsync(word.toLowerCase());
          const refreshed = resolveLemmaSync();
          if (!cancelled) {
            setNormalizedLemma(refreshed);
          }
        } catch (error) {
          if (!cancelled && syncLemmaInfo.value) {
            setNormalizedLemma(syncLemmaInfo);
          }
        }
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [resolveLemmaSync, word]);
  
  // Initialize inputRefs array based on word length
  useEffect(() => {
    inputRefs.current = Array(word.length).fill(null);
  }, [word.length]);
  
  // Detect iOS on mount
  useEffect(() => {
    setIsIOS(isIOSDevice());
  }, []);
  
  useEffect(() => {
    const applyVolume = (pool: HTMLAudioElement[]) => {
      pool.forEach(audio => {
        audio.volume = clampedFeedbackVolume;
      });
    };

    applyVolume(typingSoundPoolRef.current);
    applyVolume(beepSoundPoolRef.current);
    if (audioRef.current) {
      audioRef.current.volume = clampedFeedbackVolume;
    }
    if (beepAudioRef.current) {
      beepAudioRef.current.volume = clampedFeedbackVolume;
    }
  }, [clampedFeedbackVolume]);
  
  // Make the ref passed in point to our container
  React.useImperativeHandle(ref, () => containerRef.current as HTMLDivElement, []);

  useEffect(() => {
    return () => {
      speakAbortRef.current?.abort();
    };
  }, []);
  
  // Initialize the audio elements
  useEffect(() => {
    const typingAudio = new Audio('/sounds/key-sound/Default.wav');
    typingAudio.preload = 'auto';
    typingAudio.load();
    typingAudio.volume = clampedFeedbackVolume;

    const beepAudio = new Audio('/sounds/beep.wav');
    beepAudio.preload = 'auto';
    beepAudio.load();
    beepAudio.volume = clampedFeedbackVolume;

    const createPool = (base: HTMLAudioElement, size: number) => {
      const pool: HTMLAudioElement[] = [base];
      for (let i = 1; i < size; i++) {
        const clone = base.cloneNode(true) as HTMLAudioElement;
        clone.preload = 'auto';
        clone.load();
        clone.volume = clampedFeedbackVolume;
        pool.push(clone);
      }
      return pool;
    };

    audioRef.current = typingAudio;
    beepAudioRef.current = beepAudio;
    typingSoundPoolRef.current = createPool(typingAudio, TYPING_POOL_INITIAL_SIZE);
    beepSoundPoolRef.current = createPool(beepAudio, BEEP_POOL_INITIAL_SIZE);
    typingSoundIndexRef.current = 0;
    beepSoundIndexRef.current = 0;

    return () => {
      typingSoundPoolRef.current.forEach(audio => {
        audio.pause();
        audio.src = '';
      });
      beepSoundPoolRef.current.forEach(audio => {
        audio.pause();
        audio.src = '';
      });
      typingSoundPoolRef.current = [];
      beepSoundPoolRef.current = [];
      audioRef.current = null;
      beepAudioRef.current = null;
      typingSoundIndexRef.current = 0;
      beepSoundIndexRef.current = 0;
    };
  }, [clampedFeedbackVolume]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const AudioContextConstructor = (window.AudioContext ||
      (window as any)?.webkitAudioContext) as (new () => AudioContext) | undefined;
    if (!AudioContextConstructor) return;

    const ctx: AudioContext = new AudioContextConstructor();
    audioContextRef.current = ctx;
    let cancelled = false;

    const decodeBuffer = async (url: string, assign: (buffer: AudioBuffer) => void) => {
      try {
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        if (cancelled) return;

        const buffer = await new Promise<AudioBuffer>((resolve, reject) => {
          ctx.decodeAudioData(arrayBuffer.slice(0), resolve, reject);
        });

        if (!cancelled) {
          assign(buffer);
        }
      } catch (error) {
        console.warn(`Failed to load audio buffer for ${url}:`, error);
      }
    };

    decodeBuffer('/sounds/key-sound/Default.wav', (buffer) => {
      typingBufferRef.current = buffer;
    });

    decodeBuffer('/sounds/beep.wav', (buffer) => {
      beepBufferRef.current = buffer;
    });

    return () => {
      cancelled = true;
      typingBufferRef.current = null;
      beepBufferRef.current = null;
      if (ctx.state !== 'closed') {
        ctx.close().catch(() => {});
      }
      audioContextRef.current = null;
    };
  }, []);
  
  // When the component is completed, notify parent after a delay
  useEffect(() => {
    if (isCompleted && onAutoComplete) {
      const timer = setTimeout(() => {
        onAutoComplete();
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [isCompleted, onAutoComplete]);
  
  // Apply focus when autoFocus is true
  useEffect(() => {
    if (autoFocus) {
      if (isIOS && singleInputRef.current) {
        singleInputRef.current.focus();
      } else if (containerRef.current) {
        containerRef.current.focus();
      }
    }
  }, [autoFocus, isIOS]);
  
  const playAudioFromPool = useCallback((
    poolRef: React.MutableRefObject<HTMLAudioElement[]>,
    indexRef: React.MutableRefObject<number>,
    fallbackRef: React.MutableRefObject<HTMLAudioElement | null>,
    label: string,
    maxPoolSize: number
  ) => {
    let audioElement: HTMLAudioElement | null = null;
    const pool = poolRef.current;

    if (pool.length > 0) {
      audioElement = pool.find(sound => sound.paused || sound.ended) ?? null;

      if (!audioElement && pool.length < maxPoolSize) {
        const template = pool[0];
        const clone = template.cloneNode(true) as HTMLAudioElement;
        clone.preload = 'auto';
        clone.load();
        clone.volume = clampedFeedbackVolume;
        pool.push(clone);
        audioElement = clone;
      }

      if (!audioElement) {
        const fallbackIndex = indexRef.current % pool.length;
        audioElement = pool[fallbackIndex];
        indexRef.current = (fallbackIndex + 1) % pool.length;
      } else {
        const resolvedIndex = pool.indexOf(audioElement);
        indexRef.current = resolvedIndex >= 0 ? (resolvedIndex + 1) % pool.length : 0;
      }
    } else {
      audioElement = fallbackRef.current;
    }

    if (!audioElement) {
      return;
    }

    audioElement.volume = clampedFeedbackVolume;

    try {
      audioElement.currentTime = 0;
    } catch (error) {
      console.warn(`Unable to reset ${label}:`, error);
    }

    const playPromise = audioElement.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(err => console.error(`Error playing ${label}:`, err));
    }
  }, [clampedFeedbackVolume]);
  
  const playBufferWithWebAudio = useCallback((buffer: AudioBuffer | null, label: string) => {
    const ctx = audioContextRef.current;
    if (!ctx || !buffer) {
      return false;
    }

    if (ctx.state === 'suspended') {
      ctx.resume().catch(error => console.warn('Failed to resume AudioContext:', error));
    }

    try {
      const source = ctx.createBufferSource();
      const gainNode = ctx.createGain();
      gainNode.gain.value = clampedFeedbackVolume;
      source.buffer = buffer;
      source.connect(gainNode);
      gainNode.connect(ctx.destination);
      source.start(0);
      return true;
    } catch (error) {
      console.warn(`Failed to play ${label} via Web Audio:`, error);
      return false;
    }
  }, [clampedFeedbackVolume]);
  
  // Function to play typing sound
  const playTypingSound = useCallback(() => {
    if (playBufferWithWebAudio(typingBufferRef.current, 'typing sound')) {
      return;
    }
    playAudioFromPool(typingSoundPoolRef, typingSoundIndexRef, audioRef, 'typing sound', TYPING_POOL_MAX_SIZE);
  }, [playBufferWithWebAudio, playAudioFromPool]);
  
  // Function to play error beep sound
  const playBeepSound = useCallback(() => {
    if (playBufferWithWebAudio(beepBufferRef.current, 'beep sound')) {
      return;
    }
    playAudioFromPool(beepSoundPoolRef, beepSoundIndexRef, beepAudioRef, 'beep sound', BEEP_POOL_MAX_SIZE);
  }, [playBufferWithWebAudio, playAudioFromPool]);

  const speakWord = useCallback(async () => {
    if (!word) return;

    const langCode = getSpeakerLang(languageSetting || 'english') as LangCode;
    const controller = new AbortController();
    speakAbortRef.current?.abort();
    speakAbortRef.current = controller;

    try {
      await doSpeak({
        provider: defaultTTSProvider,
        text: word,
        lang: langCode,
        rate: 10,
        volume: 100,
        signal: controller.signal,
      });
    } catch (error) {
      console.warn('EdgeTTS speak failed in VocabCard, falling back to SpeechSynthesis:', error);
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(word);
        const fallbackLang = langCode2TTSLang[langCode] ?? (langCode.startsWith('en') ? 'en-US' : langCode);
        utterance.lang = fallbackLang;
        window.speechSynthesis.speak(utterance);
      }
    }
  }, [languageSetting, word]);

  const checkCompletion = useCallback((inputs: string[]) => {
    const wordLetters = word.toLowerCase().split('');
    const allCorrect = inputs.every((letter, index) => 
      wordLetters[index] === letter || 
      (wordLetters[index] === ' ' && letter === '')
    );
    
    if (allCorrect && !inputs.includes('')) {
      setIsCompleted(true);
      void speakWord();
      if (onCorrect) onCorrect();
    }
  }, [word, speakWord, onCorrect]);

  const isLetterCorrect = useCallback((index: number) => {
    return userInputs[index]?.toLowerCase() === word[index]?.toLowerCase();
  }, [userInputs, word]);

  // Handle full word input change for iOS
  const handleFullWordInputChange = useCallback((value: string) => {
    if (isCompleted) return;
    
    // 减少音效延迟：只在正确输入时播放
    const normalizedInput = value.toLowerCase();
    const normalizedWord = word.toLowerCase();
    const isCorrectSoFar = normalizedWord.startsWith(normalizedInput);
    
    // 立即播放音频反馈，避免延迟
    if (isCorrectSoFar && normalizedInput.length > 0) {
      playTypingSound();
    } else if (!isCorrectSoFar && normalizedInput.length > 0) {
      playBeepSound();
    }
    
    // 触觉反馈 (现代移动设备) - 放在音频后面避免影响音频时机
    if ('vibrate' in navigator && navigator.vibrate) {
      try {
        if (isCorrectSoFar && normalizedInput.length > 0) {
          navigator.vibrate(15); // 轻微振动表示正确
        } else if (!isCorrectSoFar && normalizedInput.length > 0) {
          navigator.vibrate([100, 50, 100]); // 错误模式振动
        }
      } catch (error) {
        console.log('Vibration not supported:', error);
      }
    }
    
    setFullWordInput(value);
    
    const newInputs = Array(word.length).fill('');
    for (let i = 0; i < Math.min(normalizedInput.length, word.length); i++) {
      newInputs[i] = normalizedInput[i];
    }
    setUserInputs(newInputs);
    
    if (normalizedInput === normalizedWord) {
      setIsCompleted(true);
      // 成功完成时的特殊振动
      if ('vibrate' in navigator && navigator.vibrate) {
        try {
          navigator.vibrate([200, 100, 200]); // 成功的庆祝振动
        } catch (error) {
          console.log('Success vibration not supported:', error);
        }
      }
      void speakWord();
      if (onCorrect) onCorrect();
    }
  }, [word, isCompleted, playTypingSound, playBeepSound, onCorrect, speakWord]);

  const handleDesktopKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (isCompleted) return;
    
    e.stopPropagation();
    
    if (/^[a-zA-Z0-9 ]$/.test(e.key)) {
      const newInputs = [...userInputs];
      if (focusIndex < newInputs.length) {
        const inputChar = e.key.toLowerCase();
        const correctChar = word[focusIndex].toLowerCase();
        
        const isCorrect = inputChar === correctChar;
        
        if (isCorrect) {
          playTypingSound();
          newInputs[focusIndex] = inputChar;
          setUserInputs(newInputs);
          setErrorIndex(null);
          
          if (focusIndex < word.length - 1) {
            setFocusIndex(focusIndex + 1);
          } else {
            checkCompletion(newInputs);
          }
        } else {
          playBeepSound();
          newInputs[focusIndex] = inputChar;
          setUserInputs(newInputs);
          setErrorIndex(focusIndex);
        }
      }
    } else if (e.key === 'Backspace') {
      playTypingSound();
      const newInputs = [...userInputs];
      if (focusIndex > 0 && newInputs[focusIndex] === '') {
        setFocusIndex(focusIndex - 1);
        newInputs[focusIndex - 1] = '';
      } else {
        newInputs[focusIndex] = '';
      }
      setUserInputs(newInputs);
      setIsCompleted(false);
      setErrorIndex(null);
    } else if (e.key === 'ArrowLeft') {
      if (focusIndex > 0) {
        setFocusIndex(focusIndex - 1);
      }
    } else if (e.key === 'ArrowRight') {
      if (focusIndex < word.length - 1) {
        setFocusIndex(focusIndex + 1);
      }
    }
  }, [userInputs, focusIndex, word, isCompleted, playTypingSound, playBeepSound, checkCompletion]);

  const handleLetterClick = useCallback((index: number) => {
    if (!isCompleted) {
      if (isIOS) {
        singleInputRef.current?.focus();
      } else {
        containerRef.current?.focus();
        setFocusIndex(index);
      }
      
      if (index !== focusIndex) {
        playTypingSound();
      }
      
      setFocusIndex(index);
    }
  }, [isCompleted, isIOS, focusIndex, playTypingSound]);

  // Focus/blur event handlers
  useEffect(() => {
    const currentRef = containerRef.current;
    if (currentRef) {
      const handleFocus = () => setIsFocused(true);
      const handleBlur = () => setIsFocused(false);
      
      currentRef.addEventListener('focus', handleFocus);
      currentRef.addEventListener('blur', handleBlur);
      
      return () => {
        currentRef.removeEventListener('focus', handleFocus);
        currentRef.removeEventListener('blur', handleBlur);
      };
    }
  }, []);

  const renderIOSInput = () => {
    const userInput = fullWordInput.toLowerCase();
    const targetWord = word.toLowerCase();
    const isInputCorrect = targetWord.startsWith(userInput);
    
    return (
      <div className="w-full flex flex-col items-center gap-3">
        <div className="flex flex-wrap justify-center gap-2">
          {word.split('').map((char, index) => {
            if (char === ' ') {
              return <span key={index} className="px-2"></span>;
            }
            
            const hasInput = index < userInputs.length && userInputs[index] !== '';
            const isCorrect = hasInput && isLetterCorrect(index);
            const showError = !isInputCorrect && index === userInput.length - 1;
            
            return (
              <div 
                key={index}
                onClick={() => handleLetterClick(index)}
                className={`inline-flex h-10 w-7 items-center justify-center text-2xl font-semibold select-none focus:outline-none focus-visible:outline-none ${
                  isCompleted
                    ? 'text-emerald-600'
                    : showError
                      ? 'text-red-500 animate-pulse'
                      : hasInput
                        ? (isCorrect ? 'text-primary' : 'text-red-500 animate-pulse')
                        : 'text-foreground/60'
                }`}
              >
                {hasInput ? (
                  <span>{index === 0 ? userInputs[index]?.toUpperCase() : userInputs[index]?.toLowerCase()}</span>
                ) : (
                  <span className="opacity-30 text-foreground/40">_</span>
                )}
              </div>
            );
          })}
        </div>
        
        <input
          ref={singleInputRef}
          className="sr-only"
          type="text"
          inputMode="text"
          value={fullWordInput}
          onChange={(e) => handleFullWordInputChange(e.target.value)}
          placeholder="Type here"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck="false"
          autoComplete="off"
          disabled={isCompleted}
        />
      </div>
    );
  };

  const renderDesktopLetters = () => {
    return word.split('').map((char, index) => {
      if (char === ' ') {
        return <span key={index} className="px-2"></span>;
      }
      
      const isLetterFocused = focusIndex === index && isFocused;
      const hasInput = userInputs[index] !== '' && userInputs[index] !== undefined;
      const isCorrect = hasInput && isLetterCorrect(index);
      const isError = errorIndex === index;

      const stateClass = isCompleted
        ? 'border-emerald-400 bg-emerald-50/80 text-emerald-600 shadow-[0_8px_20px_-12px_rgba(16,185,129,0.6)]'
        : isError
          ? 'border-red-300 bg-red-50/80 text-red-500 animate-pulse'
          : hasInput
            ? (isCorrect
                ? 'border-primary/50 bg-primary/10 text-primary'
                : 'border-red-300 bg-red-50/70 text-red-500 animate-pulse')
            : 'border-border/60 bg-white/60 text-foreground/80';
      
      return (
        <div 
          key={index}
          onClick={() => handleLetterClick(index)}
          className={`inline-flex h-10 w-7 items-center justify-center text-2xl font-semibold cursor-pointer select-none transition-colors duration-150 focus:outline-none focus-visible:outline-none ${
            isCompleted
              ? 'text-emerald-600'
              : isError
                ? 'text-red-500 animate-pulse'
                : hasInput
                  ? (isCorrect ? 'text-primary' : 'text-red-500 animate-pulse')
                  : 'text-foreground/60'
          } ${isLetterFocused && !isCompleted ? 'underline decoration-2 decoration-primary/60 underline-offset-8' : ''}`}
        >
          {hasInput || isCompleted ? (
            <span>{index === 0 ? userInputs[index]?.toUpperCase() : userInputs[index]?.toLowerCase()}</span>
          ) : (
            <span className="opacity-30 text-foreground/40">_</span>
          )}
        </div>
      );
    });
  };

  return (
    <div 
      ref={containerRef} 
      tabIndex={isIOS ? -1 : 0} 
      className={`rounded-3xl border px-4 pt-5 pb-4 sm:px-5 sm:pt-6 sm:pb-5 transition-colors backdrop-blur supports-[backdrop-filter]:bg-popover/95 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 ${isFocused ? 'border-primary/40 bg-popover/95' : 'border-border/60 bg-popover/35'}`}
      onKeyDown={isIOS ? undefined : handleDesktopKeyDown}
    >
      <div className="flex flex-col space-y-0">
        <div className="flex items-center justify-between">
          <div className="flex-1 flex justify-center">
            {isIOS ? renderIOSInput() : renderDesktopLetters()}
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-10 w-10 rounded-2xl border border-primary/2 shadow-sm backdrop-blur-sm flex-shrink-0 self-start -translate-y-2 transition-all hover:bg-primary/15 hover:border-primary/40 focus-visible:ring-primary/40" 
            onClick={() => void speakWord()}
          >
            <svg
              className="translate-x-1.5 h-8 w-8 min-h-[1.6rem] min-w-[1.6rem] max-h-none max-w-none"
              style={{ fontSize: '3rem' }}
              viewBox="0 0 1024 1024"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
            >
              <path d="M417.28 164.198c-12.646 0-25.293 5.325-37.683 15.821L169.779 358.35H76.8c-42.342 0-76.8 34.457-76.8 76.8v204.8c0 42.342 34.458 76.8 76.8 76.8h92.98l209.817 178.33c12.339 10.495 25.037 15.82 37.683 15.82a40.755 40.755 0 0034.304-18.534c6.093-9.165 9.216-20.89 9.216-34.816v-640c0-36.864-21.862-53.402-43.52-53.402zM51.2 640V435.2a25.6 25.6 0 0125.6-25.6h76.8v256H76.8A25.6 25.6 0 0151.2 640zm358.4 213.453l-204.8-174.08V395.827l204.8-174.08v631.706z"></path>
            </svg>
          </Button>
        </div>
        
        <div className="text-center mt-2">
          {isCompleted && (
            <div className="flex flex-col items-center justify-center space-y-1">
              {/* <div className="flex items-center justify-center text-green-600">
                <CheckCircle2 className="h-5 w-5 mr-2" />
                <span className="font-medium">Correct!</span>
              </div> */}
              {(translation || normalizedLemma.value) && (
                <div className="flex flex-col items-center gap-1 text-sm text-gray-600 px-2 py-1 rounded-md max-w-full">
                  {translation && (
                    <div className="w-full text-center italic">
                      {translation}
                    </div>
                  )}
                  {normalizedLemma.value && (
                    <div className="w-full text-center text-xs uppercase tracking-wide text-muted-foreground">
                      {normalizedLemma.source === 'heuristic'
                        ? t('vocabulary.baseFormSuggestedLabel', 'Base form (suggested)')
                        : t('vocabulary.baseFormLabel', 'Base form')}
                      {": "}
                      <span className="font-semibold normal-case text-foreground">{normalizedLemma.value}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* {!isCompleted && (
          <div className="flex justify-center mt-2">
            <Button
              variant="ghost"
              className="rounded-xl"
              size="sm"
              onClick={() => {
                if (isIOS) {
                  setFullWordInput(word);
                }
                setUserInputs(word.toLowerCase().split(''));
                setIsCompleted(true);
                speakWord();
                if (onCorrect) onCorrect();
              }}
            >
              Reveal Answer
            </Button>
          </div>
        )} */}
      </div>
    </div>
  );
});

VocabCard.displayName = 'VocabCard'; 
